import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { ethers } from 'ethers'
import { useTranslation } from 'react-i18next'

import { getWalletSnapshot } from '../../../../utils/walletProviders'
import { getChainSymbol, nftAbi } from '../../../../utils/config'
import { getLocalizedWalletError } from '../../../NFTConsole/errorMessages'
import ConsoleDialog from '../../../NFTConsole/ConsoleDialog'

const normalizeRates = (values) => values?.length > 0
    ? values.map((value) => value?.toString?.() || String(value || ''))
    : ['']

export default function InviteConfirmModal(props) {
    const [currentProvider, setCurrentProvider] = useState(null)
    const [rates, setRates] = useState([''])
    const [submitLoading, setSubmitLoading] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    const chainId = useSelector((state) => state.chainId)
    const { t } = useTranslation()

    useEffect(() => {
        if (!props.show) return
        setRates(normalizeRates(props.invitorRewardPercentList))
        setErrorMsg('')
    }, [props.invitorRewardPercentList, props.show])

    const totalRate = useMemo(() => rates.reduce((total, value) => {
        const parsed = Number(value)
        return total + (Number.isFinite(parsed) ? parsed : 0)
    }, 0), [rates])

    const initProvider = () => {
        if (currentProvider) return currentProvider
        const injectedProvider = getWalletSnapshot().provider
        if (!injectedProvider) throw new Error(t('请先连接钱包再操作'))
        const provider = new ethers.providers.Web3Provider(injectedProvider)
        setCurrentProvider(provider)
        return provider
    }

    const updateRate = (index, value) => {
        setRates((current) => current.map((rate, rateIndex) => rateIndex === index ? value : rate))
        setErrorMsg('')
    }

    const removeLevel = (index) => {
        setRates((current) => current.filter((_, rateIndex) => rateIndex !== index))
        setErrorMsg('')
    }

    const addLevel = () => {
        if (rates.length >= 7) {
            setErrorMsg(t('最多可设置7级推广'))
            return
        }
        setRates((current) => [...current, ''])
    }

    const submit = async () => {
        setErrorMsg('')
        if (rates.length < 1) {
            setErrorMsg(t('请设置推广算力'))
            return
        }

        for (let index = 0; index < rates.length; index += 1) {
            const value = rates[index].trim()
            if (!value) {
                setErrorMsg(t('请设置1级推广算力').replace('{index}', index + 1))
                return
            }
            if (!/^\d+(\.\d{1,2})?$/.test(value) || Number(value) < 0 || Number(value) > 100) {
                setErrorMsg(t('1级推广算力应在0-100之间').replace('{index}', index + 1))
                return
            }
        }

        if (totalRate > 100) {
            setErrorMsg(t('推广算力总和不能超过100%'))
            return
        }

        setSubmitLoading(true)
        try {
            const provider = initProvider()
            const contract = new ethers.Contract(props.poolAddress, nftAbi, provider.getSigner())
            const encodedRates = rates.map((value) => ethers.utils.parseUnits(value, 2))
            const transaction = props.open
                ? await contract.setInvitorRewardPercentList(encodedRates)
                : await contract.openInviter(encodedRates, { value: props.fee })
            await transaction.wait()
            props.ok?.()
        } catch (error) {
            setErrorMsg(getLocalizedWalletError(error, t))
        } finally {
            setSubmitLoading(false)
        }
    }

    return <ConsoleDialog
        show={props.show}
        onClose={() => !submitLoading && props.handleClose?.()}
        title={props.open ? t('设置推广返利等级') : t('开启推广邀请')}
        description={t('配置各级邀请人可获得的铸造返利比例')}
        icon="fa-users"
        closeLabel={t('关闭')}
        className="referral-dialog"
        footer={<>
            <button type="button" className="console-button is-secondary" disabled={submitLoading} onClick={() => props.handleClose?.()}>{t('取消')}</button>
            <button type="button" className="console-button is-primary" disabled={submitLoading} onClick={submit}>
                {submitLoading && <i className="fa fa-spinner loading-css" />} {props.open ? t('保存设置') : t('开启推广邀请')}
            </button>
        </>}
    >
        <div className="referral-intro">
            <span><i className="fa fa-project-diagram" /></span>
            <div>
                <strong>{t('推广返利规则')}</strong>
                <p>{t('每一级比例表示该层邀请人从单次铸造金额中获得的返利，总和不能超过 100%。')}</p>
                <small>{t('例如，一级设置 5%，用户支付 1 个支付代币时，一级邀请人获得 0.05。')}</small>
            </div>
        </div>

        <div className="referral-levels">
            <div className="referral-levels__heading">
                <strong>{t('推广层级')}</strong>
                <span>{rates.length} / 7</span>
            </div>
            {rates.map((rate, index) => <div className="referral-level-row" key={`referral-level-${index}`}>
                <span className="referral-level-row__index">L{index + 1}</span>
                <label>
                    <span>{t('第{index}级邀请返利比例').replace('{index}', index + 1)}</span>
                    <div>
                        <input value={rate} onChange={(event) => updateRate(index, event.target.value.trim())} inputMode="decimal" placeholder="0" />
                        <em>%</em>
                    </div>
                </label>
                {index > 0 && <button type="button" onClick={() => removeLevel(index)} aria-label={t('删除第{index}级').replace('{index}', index + 1)} title={t('删除第{index}级').replace('{index}', index + 1)}><i className="fa fa-trash-alt" /></button>}
            </div>)}
            {rates.length < 7 && <button type="button" className="referral-add-level" onClick={addLevel}><i className="fa fa-plus" /> {t('新增下级')}</button>}
        </div>

        <div className="referral-summary-card">
            <span><small>{t('累计返利比例')}</small><strong className={totalRate > 100 ? 'is-danger' : ''}>{totalRate.toFixed(2)}%</strong></span>
            {!props.open && <span><small>{t('开启费用')}</small><strong>{ethers.utils.formatEther(props.fee || 0)} {getChainSymbol(chainId)}</strong></span>}
        </div>

        {errorMsg && <div className="console-inline-error" role="alert"><i className="fa fa-exclamation-circle" /> {errorMsg}</div>}
    </ConsoleDialog>
}
